import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLandscapeVideo } from "@/hooks/useIsFullscreen";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useQuery } from "@tanstack/react-query";
import { Button, Spinner } from "@safers/ui";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetBody } from "@safers/ui/organisms";
import { useAuthStore, selectUser, canAccessSite, canAccessMenuSite } from "@safers/auth";
import { cctvPath, controlPath } from "@/routes/paths";
import { eventsService, isCctvEventCategory, severityChip, type Event } from "@/services";
import { SEVERITY_LABELS } from "@/lib/map";
import { typeLabel, categoryLabel } from "@/lib/eventLabels";

const eventKeys = {
  detail: (id: number) => ["event", id] as const,
};

function formatDateTime(timestamp: string): string {
  const d = new Date(timestamp);
  if (!Number.isFinite(d.getTime())) return "—";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function Media({ event }: { event: Event }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  useLandscapeVideo(boxRef, isMobile);

  if (event.video?.url) {
    return (
      <div ref={boxRef} className="overflow-hidden rounded-lg bg-black">
        <video
          src={event.video.url}
          controls
          autoPlay
          muted
          playsInline
          className="max-h-[20rem] w-full object-contain sm:max-h-[50vh]"
        />
      </div>
    );
  }
  if (event.snapshot?.url) {
    return (
      <div className="overflow-hidden rounded-lg bg-neutral-100">
        <img
          src={event.snapshot.url}
          alt={`${event.name} 스냅샷`}
          className="max-h-[20rem] w-full object-contain sm:max-h-[50vh]"
        />
      </div>
    );
  }
  return (
    <div className="flex h-[8rem] items-center justify-center rounded-lg bg-neutral-50 text-[0.8125rem] text-neutral-500">
      미디어 없음
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-3 py-1.5">
      <span className="w-16 flex-none text-[0.8125rem] text-neutral-500">{label}</span>
      <span className="min-w-0 flex-1 truncate text-[0.8125rem] font-semibold text-neutral-900">
        {value}
      </span>
    </div>
  );
}

function useEventDetail(eventId: number | null) {
  return useQuery({
    queryKey: eventKeys.detail(eventId ?? -1),
    queryFn: () => eventsService.getEvent(eventId as number).then((r) => r.data),
    enabled: eventId != null,
    staleTime: Infinity,
  });
}

export function EventDetailBody({
  eventId,
  onNavigate,
}: {
  eventId: number | null;
  onNavigate?: () => void;
}) {
  const navigate = useNavigate();
  const user = useAuthStore(selectUser);
  const { data, isLoading, isError } = useEventDetail(eventId);

  const siteId = data?.site?.id ?? null;
  const canSite = siteId != null && canAccessSite(user, siteId);
  const canCctv =
    siteId != null &&
    isCctvEventCategory(data?.category) &&
    canAccessMenuSite(user, siteId, "CCTV");

  const camStream = canCctv && data?.path ? data.path : null;

  const go = (path: string) => {
    onNavigate?.();
    navigate(path, { replace: true });
  };

  const cctvHref =
    siteId == null
      ? null
      : `${cctvPath(siteId)}${camStream ? `?cam=${encodeURIComponent(camStream)}` : ""}`;

  return (
    <>
      {isLoading && (
        <div className="flex h-[12rem] items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}

      {isError && (
        <div className="flex h-[12rem] flex-col items-center justify-center gap-1">
          <p className="text-sm font-bold text-neutral-900">이벤트를 불러오지 못했습니다</p>
          <p className="text-[0.8125rem] text-neutral-500">
            삭제되었거나 접근 권한이 없을 수 있습니다.
          </p>
        </div>
      )}

      {data && (
        <div className="flex flex-col gap-3">
          <Media event={data} />
          <div className="divide-y divide-neutral-100">
            <InfoRow label="현장" value={data.site?.name ?? "—"} />
            <InfoRow label="유형" value={typeLabel(data.type)} />
            <InfoRow label="구분" value={categoryLabel(data.category)} />
            <InfoRow label="발생" value={formatDateTime(data.timestamp)} />
            {Number.isFinite(data.confidence) && data.confidence > 0 && (
              <InfoRow label="신뢰도" value={`${Math.round(data.confidence * 100)}%`} />
            )}
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              size="sm"
              variant="outline"
              disabled={!canSite}
              title={canSite ? undefined : "이 현장의 접근 권한이 없습니다"}
              onClick={() => siteId != null && go(controlPath(siteId))}
            >
              현장으로 이동
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!canCctv}
              title={canCctv ? undefined : "이 현장의 CCTV 권한이 없거나 영상 이벤트가 아닙니다"}
              onClick={() => cctvHref && go(cctvHref)}
            >
              CCTV에서 보기
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export function EventDetailHeading({ eventId }: { eventId: number | null }) {
  const { data: event } = useEventDetail(eventId);
  if (!event) return <>이벤트 상세</>;
  return (
    <span className="flex min-w-0 items-center gap-2">
      {event.severity && (
        <span
          className={`flex-none rounded px-1.5 py-0.5 text-[0.6875rem] font-bold ${severityChip(event.severity).bg} ${severityChip(event.severity).text}`}
        >
          {SEVERITY_LABELS[event.severity]}
        </span>
      )}
      <span className="truncate">{event.name}</span>
    </span>
  );
}

export function EventDetailSheet({
  eventId,
  onClose,
}: {
  eventId: number | null;
  onClose: () => void;
}) {
  const isMobile = useIsMobile();

  return (
    <Sheet open={eventId != null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side={isMobile ? "bottom" : "right"}
        aria-describedby={undefined}
        className={
          isMobile
            ? "max-h-[85dvh] rounded-t-2xl"
            : "w-full sm:w-[38rem] sm:max-w-[92vw] xl:w-[48rem]"
        }
      >
        <SheetHeader className="p-4 pb-3 pr-14 sm:p-5 sm:pb-3 sm:pr-14">
          <SheetTitle className="text-base">
            <EventDetailHeading eventId={eventId} />
          </SheetTitle>
        </SheetHeader>

        <SheetBody className="px-4 pb-5 sm:px-5">
          <EventDetailBody eventId={eventId} />
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
